import {
  ComWixEcomMembershipsSpiV1HostMembership,
  EcomCheckoutV1ItemAvailabilityStatus,
  EnrichedLineItemFragment,
  ItemAvailabilityInfoFragment,
  LineItemFragment,
  MembershipOptionsFragment,
} from '../../../../gql/graphql';
import {QuantityInfoModel} from './QuantityInfo.model';
import {PricingModel} from './Pricing.model';
import {SourceModel} from './Source.model';
import {AttributesModel} from './Attributes.model';
import {PaymentConfigModel} from './PaymentConfig.model';
import {ModifierGroupModel} from './ModifierGroup.model';
import {ModelContext} from '../../../../types/checkoutApp.types';
import {V2LineItem as LineItem} from '@wix/auto_sdk_ecom_cart-v-2';

export class LineItemModel {
  public id: string;
  public name: string;
  public quantityInfo: QuantityInfoModel;
  public pricing: PricingModel;
  public source: SourceModel;
  public attributes: AttributesModel;
  public paymentConfig: PaymentConfigModel;
  public modifierGroups: ModifierGroupModel[];

  constructor(params: LineItemModel) {
    this.id = params.id;
    this.name = params.name;
    this.quantityInfo = params.quantityInfo;
    this.pricing = params.pricing;
    this.source = params.source;
    this.attributes = params.attributes;
    this.paymentConfig = params.paymentConfig;
    this.modifierGroups = params.modifierGroups;
  }

  public static fromCheckoutV1(
    lineItem: LineItemFragment,
    enrichedLineItems: (EnrichedLineItemFragment | null)[] | undefined | null,
    membershipOptions: MembershipOptionsFragment,
    modelContext: ModelContext
  ): LineItemModel {
    const enrichedItem = findEnrichedItem(enrichedLineItems, lineItem.id);
    const mergedLineItem = mergeEnrichedItem(lineItem, enrichedItem);
    const {
      id,
      media,
      productName,
      lineItemPrice,
      price,
      fullPrice,
      priceBeforeDiscounts,
      catalogReference,
      quantity,
      subscriptionOptionInfo,
      itemType,
      availability,
      physicalProperties,
      descriptionLines,
      paymentOption,
      depositAmount,
    } = mergedLineItem;

    const name = productName?.translated ?? productName?.original ?? '';
    const confirmedQuantity = calculateQuantity(availability!, Number(quantity));
    const selectedMembership = findSelectedMembership(membershipOptions, lineItem.id);

    return new LineItemModel({
      id: id!,
      name,
      quantityInfo: QuantityInfoModel.fromCheckoutV1(
        confirmedQuantity,
        lineItem.fixedQuantity,
        enrichedItem?.renderingConfig?.hideQuantity
      ),
      pricing: PricingModel.fromCheckoutV1({
        unitPrice: price,
        totalPrice: lineItemPrice,
        priceUndetermined: lineItem.priceUndetermined,
        fullPrice,
        salePrice: priceBeforeDiscounts,
        totalModifiersPrice: lineItem.modifiersTotalPrice,
        confirmedQuantity,
        hidePrice: enrichedItem?.renderingConfig?.hidePrice,
        modelContext,
      }),
      source: SourceModel.fromCheckoutV1(catalogReference),
      attributes: AttributesModel.fromCheckoutV1({
        name,
        itemType: itemType!,
        descriptionLines,
        image: media,
        policies: lineItem.policies,
        physicalProperties,
        subscriptionOptionInfo,
        membersOnly: lineItem.membersOnly,
      }),
      paymentConfig: PaymentConfigModel.fromCheckoutV1({
        savePaymentMethod: lineItem.savePaymentMethod,
        consentRequiredPaymentPolicy: lineItem.consentRequiredPaymentPolicy,
        paymentOption,
        depositAmount,
        selectedMembership,
        modelContext,
      }),
      modifierGroups:
        lineItem.modifierGroups?.map((modifierGroup) =>
          ModifierGroupModel.fromCheckoutV1(modifierGroup!, modelContext)
        ) ?? [],
    });
  }

  public static fromCartV2(lineItem: LineItem, modelContext: ModelContext): LineItemModel {
    const name =
      lineItem.name?.translated ?? /* istanbul ignore next */ lineItem.name?.original ?? /* istanbul ignore next */ '';
    return new LineItemModel({
      id: lineItem._id!,
      name,
      quantityInfo: QuantityInfoModel.fromCartV2(lineItem.quantityInfo!),
      pricing: PricingModel.fromCartV2(lineItem.pricing!, lineItem.quantityInfo!.confirmedQuantity!, modelContext),
      source: SourceModel.fromCartV2(lineItem.source!),
      attributes: AttributesModel.fromCartV2(lineItem.attributes!, name),
      paymentConfig: PaymentConfigModel.fromCartV2(lineItem.paymentConfig!, modelContext),
      modifierGroups:
        /* istanbul ignore next */ lineItem.modifierGroups?.map((modifierGroup) =>
          ModifierGroupModel.fromCartV2(modifierGroup, modelContext)
        ) ?? [],
    });
  }
}

function findEnrichedItem(enrichedLineItems?: (EnrichedLineItemFragment | null)[] | null, lineItemId?: string | null) {
  if (!enrichedLineItems) {
    return undefined;
  }
  return enrichedLineItems.find((enrichedItem) => enrichedItem?.id === lineItemId);
}

export function mergeEnrichedItem(lineItem: LineItemFragment, enrichedItem?: EnrichedLineItemFragment | null) {
  if (!enrichedItem) {
    return lineItem;
  }
  const overriddenDescriptionLines = enrichedItem?.overriddenDescriptionLines?.descriptionLines;
  return {
    ...lineItem,
    ...enrichedItem,
    ...(overriddenDescriptionLines ? {descriptionLines: overriddenDescriptionLines} : /* istanbul ignore next */ {}),
  };
}

function calculateQuantity(availability: ItemAvailabilityInfoFragment, quantity: number): number {
  if (availability.status === EcomCheckoutV1ItemAvailabilityStatus.NOT_AVAILABLE) {
    return 0;
  }
  if (availability.status === EcomCheckoutV1ItemAvailabilityStatus.PARTIALLY_AVAILABLE) {
    return availability.quantityAvailable!;
  }
  return quantity;
}

function findSelectedMembership(
  membershipOptions?: MembershipOptionsFragment | null,
  lineItemId?: string | null
): ComWixEcomMembershipsSpiV1HostMembership | undefined {
  if (!membershipOptions || !lineItemId) {
    return undefined;
  }
  return (
    membershipOptions.eligibleMemberships?.find((membership) => membership?.lineItemIds?.includes(lineItemId)) ??
    /* istanbul ignore next */ undefined
  );
}
